import { NetworkStatus, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  ControlledDateFilterControl,
  ControlledDateFilterSummary,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateWithDelta,
  type FacetedFilterOption,
  Number,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Txt,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { formatLocalDateComponents, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { compareAsc } from "date-fns";
import { X } from "lucide-react";
import { type ReactNode, Suspense, useState } from "react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import {
  RiAddLine,
  RiBox2Line,
  RiBuilding2Line,
  RiCalendarLine,
  RiFilter3Line,
  RiPriceTagLine,
  RiWallet2Line,
} from "react-icons/ri";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  destinationAccountKindValues,
  transferPageOrderValues,
  transferStatusValues,
} from "~/gql/client/enumValues";
import type {
  AdminTransferRowFragment,
  TransferPageOrder,
  TransfersFilter,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/transfers")({
  head: () => opsHead("Transfers"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { useSearchParams } = useRouter();

  graphql(`
    fragment AdminTransferRow on Transfer {
      id
      requestedForDate
      currencyAmount {
        currency
        amount
      }
      unitAmount {
        amount
        unit
      }
      status
      sourceAsset {
        ...AssetCardFields
      }
      destinationAccount {
        ... on Asset {
          ...AssetCardFields
        }
        ... on BeneficiaryAccount {
          ...BeneficiaryAssetCardFields
        }
      }
      matchedDestinationTransactions {
        id
        effectiveAt
        direction
        currencyAmount {
          currency
          amount
        }
        ledgerAccount {
          id
          ... on AssetLedgerAccount {
            asset {
              id
              ...AssetCardFields
            }
          }
        }
      }
    }
  `);

  const transfersDocument = graphql(`
    query AdminTransfers($after: String, $first: Int!, $order: TransferPageOrder, $filter: TransfersFilter!) {
      transfers(after: $after, first: $first, order: $order, filter: $filter) {
        pageInfo {
          endCursor
          hasNextPage
        }
        transfers {
          ...AdminTransferRow
        }
      }
    }
  `);

  const [filterAndSortState, setFilterAndSortOrder] =
    useQueryPersistedStateWithSchema({
      useSearchParams,
      schema: z.object({
        destinationAssetIds: z.array(z.string()).optional().nullable(),
        destinationProductIds: z.array(z.string()).optional().nullable(),
        endDateExclusive: z.date().optional().nullable(),
        organisationIds: z.array(z.string()).optional().nullable(),
        sourceAssetIds: z.array(z.string()).optional().nullable(),
        sourceProductIds: z.array(z.string()).optional().nullable(),
        startDateInclusive: z.date().optional().nullable(),
        statuses: z.array(z.enum(transferStatusValues)).optional().nullable(),
        sortOrder: z.enum(transferPageOrderValues).optional().nullable(),
        destinationAccountKinds: z
          .enum(destinationAccountKindValues)
          .array()
          .optional()
          .nullable(),
      }),
      defaultState: {
        sortOrder: "DATE_DESC",
      },
    });

  const { sortOrder, ...filter } = filterAndSortState;

  const baseInput = {
    first: 20,
    order: sortOrder,
    filter,
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    transfersDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const transfers = data?.transfers?.transfers ?? [];
  const nextCursor = data?.transfers?.pageInfo.hasNextPage
    ? (data.transfers.pageInfo.endCursor ?? undefined)
    : undefined;

  const handleSortChange = (newSortingState: SortingState) => {
    const primarySort = newSortingState.at(0);
    let newSortOrder: TransferPageOrder;
    if (primarySort?.id === "date") {
      newSortOrder = primarySort.desc ? "DATE_DESC" : "DATE_ASC";
    } else {
      newSortOrder = "DATE_DESC";
    }
    if (newSortOrder !== sortOrder) {
      setFilterAndSortOrder({
        ...filterAndSortState,
        sortOrder: newSortOrder,
      });
    }
  };

  const { data: pageData } = useSuspenseQuery(
    graphql(`
      query AdminTransfersPage {
        organisations {
          id
          name
        }
        products {
          id
          internalName
          name
        }
      }
    `),
  );

  const organisationOptions: FacetedFilterOption[] = (
    pageData?.organisations?.slice() ?? []
  )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((o) => ({
      value: o.id,
      searchValues: [o.name],
      label: <div>{o.name}</div>,
    }));

  const productOptions: FacetedFilterOption[] = (
    pageData?.products?.slice() ?? []
  )
    .sort((a, b) => a.internalName.localeCompare(b.internalName))
    .map((p) => ({
      value: p.id,
      searchValues: [p.internalName, p.name],
      label: <div>{p.internalName}</div>,
    }));

  return (
    <AdminPageContainer testId="admin-transfers">
      <AdminPageHeader
        title="Transfers"
        subtitle="Move funds between accounts"
        buttons={
          <div className="flex gap-2">
            <Link to="/transfers/new">
              <Button leftIcon={<RiAddLine />}>New transfer</Button>
            </Link>
          </div>
        }
      />
      <FilterBar
        filterValue={filter}
        onFilterChange={(values) => {
          setFilterAndSortOrder({
            ...values,
            sortOrder,
          });
        }}
        organisationOptions={organisationOptions}
        productOptions={productOptions}
      />
      <DataTable
        data={transfers}
        columns={columns}
        isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
        manualSorting={true}
        manualPagination={true}
        maxMultiSortColCount={1}
        hidePagination={true}
        pageSize={10_000}
        className="overflow-hidden border"
        linkTo={(row) => `/transfers/${row.original.id}`}
        onSortingChange={handleSortChange}
        columnSortProps={{
          id: "date",
          desc: true,
        }}
        initialColumnVisibility={{
          id: false,
        }}
      />
      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({ variables: { ...baseInput, after: nextCursor } })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!transfers.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </AdminPageContainer>
  );
}

const columns: ColumnDef<AdminTransferRowFragment & { id: string }>[] = [
  {
    header: "ID",
    accessorKey: "id",
    cell: () => null,
  },
  {
    accessorKey: "date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      return <DateWithDelta date={row.original.requestedForDate} />;
    },
    sortingFn: (a, b) =>
      compareAsc(a.original.requestedForDate, b.original.requestedForDate),
  },
  {
    accessorKey: "sourceAsset",
    header: "From",
    cell: ({ row }) => {
      return row.original.sourceAsset ? (
        <AccountCard
          subText={["BUSINESS_ENTITY", "IDENTIFIER"]}
          account={row.original.sourceAsset}
        />
      ) : null;
    },
  },
  {
    accessorKey: "destinationAccount",
    header: "To",
    cell: ({ row }) => {
      const dest = row.original.destinationAccount;
      if (!dest) {
        return null;
      }
      if (dest.__typename === "BeneficiaryAccount") {
        return (
          <AccountCard
            subText={["BUSINESS_ENTITY", "IDENTIFIER"]}
            account={dest}
          />
        );
      }
      return (
        <AccountCard
          subText={["BUSINESS_ENTITY", "IDENTIFIER"]}
          account={dest}
        />
      );
    },
  },
  {
    id: "amount",
    accessorFn: () => undefined,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Amount"
        align="right"
        className="flex flex-row justify-end pr-2"
      />
    ),
    cell: ({ row }) => {
      if (!row.original.currencyAmount) {
        return null;
      }
      const { amount, currency } = row.original.currencyAmount;

      return (
        <div className="flex flex-row justify-between gap-2">
          {row.original.unitAmount &&
            row.original.unitAmount.unit !== currency && (
              <TooltipProvider delayDuration={50}>
                <Tooltip>
                  <TooltipTrigger>
                    <HiOutlineInformationCircle />
                  </TooltipTrigger>
                  <TooltipContent>
                    {row.original.unitAmount && (
                      <Number
                        value={row.original.unitAmount.amount}
                        format={Number.format.units(
                          row.original.unitAmount.amount,
                        )}
                      />
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

          <div className="w-full text-right">
            <span className="flex flex-row justify-end gap-1">
              <CurrencyAmountUI amount={amount} currency={currency} />
            </span>
          </div>
        </div>
      );
    },
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const { status, destinationAccount, matchedDestinationTransactions } =
        row.original;
      if (!status) {
        return null;
      }
      if (destinationAccount?.__typename !== "BeneficiaryAccount") {
        return <StatusUI status={status} />;
      }

      let tooltipContent: ReactNode;
      if (!matchedDestinationTransactions) {
        tooltipContent = (
          <Txt variant="value">
            No linked external account or source transaction
          </Txt>
        );
      } else if (matchedDestinationTransactions.length === 0) {
        tooltipContent = <Txt variant="value">No matched transactions</Txt>;
      } else {
        tooltipContent = (
          <div className="flex flex-col gap-2">
            <Txt variant="value">Matched destination transactions</Txt>
            <div className="grid grid-cols-[auto_auto_auto]">
              {matchedDestinationTransactions.map((txn) => (
                <div
                  key={txn.id}
                  className="col-span-3 grid grid-cols-subgrid items-center gap-x-4 rounded-md px-2 py-1 odd:bg-white even:bg-neutral-muted"
                >
                  <div className="flex items-center gap-2">
                    {txn.ledgerAccount?.__typename === "AssetLedgerAccount" &&
                    txn.ledgerAccount.asset ? (
                      <AccountCard
                        variant="INLINE_TEXT"
                        subText="MASKED_IDENTIFIER"
                        account={txn.ledgerAccount.asset}
                      />
                    ) : (
                      "-"
                    )}
                  </div>
                  <Txt variant="description">
                    {formatLocalDateComponents(txn.effectiveAt).datetime}
                  </Txt>
                  <div className="justify-self-end">
                    <CurrencyAmountUI
                      amount={txn.currencyAmount.amount.mul(
                        txn.direction === "DEBIT" ? -1 : 1,
                      )}
                      currency={txn.currencyAmount.currency}
                      showSign
                      nonCopyable
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      return (
        <Tooltip delayDuration={250}>
          <TooltipTrigger>
            <StatusUI status={status} />
          </TooltipTrigger>
          <TooltipContent className="rounded-lg border bg-white p-2 text-left text-primary shadow-surface">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      );
    },
  },
];

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
  productOptions,
}: {
  filterValue: TransfersFilter;
  onFilterChange: (_: TransfersFilter) => void;
  organisationOptions: FacetedFilterOption[];
  productOptions: FacetedFilterOption[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("date");
  const filterCount =
    0 +
    ((filterValue?.organisationIds?.length ?? 0) > 0 ? 1 : 0) +
    (filterValue.startDateInclusive || filterValue.endDateExclusive ? 0 : 0) +
    ((filterValue?.destinationAccountKinds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.sourceProductIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.destinationProductIds?.length ?? 0) > 0 ? 1 : 0);

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      startDateInclusive: undefined,
      endDateExclusive: undefined,
      organisationIds: undefined,
      statuses: undefined,
      destinationAccountKinds: undefined,
      sourceProductIds: undefined,
      destinationProductIds: undefined,
    });
  };

  const statusOptions = transferStatusValues.map((k) => ({
    value: k,
    label: <span>{k}</span>,
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("date")}
              >
                <RiCalendarLine />
                <div className="flex flex-1">Date</div>
                {(filterValue.endDateExclusive ||
                  filterValue.startDateInclusive) && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("status")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.statuses?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("org")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("destinationAccountKinds")}
              >
                <RiWallet2Line />
                <div className="flex flex-1">Destination Account Kind</div>
                {(filterValue.destinationAccountKinds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("sourceProductIds")}
              >
                <RiBox2Line />
                <div className="flex flex-1">Source Product</div>
                {(filterValue.sourceProductIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("destinationProductIds")}
              >
                <RiBox2Line />
                <div className="flex flex-1">Destination Product</div>
                {(filterValue.destinationProductIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "date" && (
              <ControlledDateFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    startDateInclusive: value?.startAtInclusive,
                    endDateExclusive: value?.endAtExclusive,
                  });
                }}
                filterValue={{
                  startAtInclusive: filterValue.startDateInclusive ?? undefined,
                  endAtExclusive: filterValue.endDateExclusive ?? undefined,
                }}
              />
            )}
            {path === "statuses" && (
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filterValue.statuses ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    statuses: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "organisation" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues.filter((id) => id !== null)
                      : undefined,
                  });
                }}
              />
            )}
            {path === "destinationAccountKinds" && (
              <ControlledFacetedFilterControl
                options={destinationAccountKindValues.map((v) => ({
                  label: prettyPrintEnum(v),
                  value: v,
                }))}
                selectedValues={filterValue.destinationAccountKinds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    destinationAccountKinds: newSelectedValues.length
                      ? newSelectedValues.filter((id) => id !== null)
                      : undefined,
                  });
                }}
              />
            )}
            {path === "sourceProductIds" && (
              <ControlledFacetedFilterControl
                options={productOptions}
                selectedValues={filterValue.sourceProductIds ?? []}
                onChange={(newSelectedValues) => {
                  const values = newSelectedValues.filter(
                    (id): id is string => id !== null,
                  );
                  onFilterChange({
                    ...filterValue,
                    sourceProductIds: values.length ? values : undefined,
                  });
                }}
              />
            )}
            {path === "destinationProductIds" && (
              <ControlledFacetedFilterControl
                options={productOptions}
                selectedValues={filterValue.destinationProductIds ?? []}
                onChange={(newSelectedValues) => {
                  const values = newSelectedValues.filter(
                    (id): id is string => id !== null,
                  );
                  onFilterChange({
                    ...filterValue,
                    destinationProductIds: values.length ? values : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledDateFilterSummary
        onClick={() => {
          setPath("date");
          setOpen(true);
        }}
        value={{
          startAtInclusive: filterValue.startDateInclusive ?? undefined,
          endAtExclusive: filterValue.endDateExclusive ?? undefined,
        }}
      />

      <ControlledFactedFilterSummary
        title="Status"
        options={statusOptions}
        selectedValues={filterValue.statuses ?? []}
        onClick={() => {
          setPath("statuses");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("organisation");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Destination account kind"
        options={destinationAccountKindValues.map((v) => ({
          label: prettyPrintEnum(v),
          value: v,
        }))}
        selectedValues={filterValue.destinationAccountKinds ?? []}
        onClick={() => {
          setPath("destinationAccountKinds");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Source product"
        options={productOptions}
        selectedValues={filterValue.sourceProductIds ?? []}
        onClick={() => {
          setPath("sourceProductIds");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Destination product"
        options={productOptions}
        selectedValues={filterValue.destinationProductIds ?? []}
        onClick={() => {
          setPath("destinationProductIds");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
